import React, { useEffect, useRef, useState } from 'react';

import _ from 'lodash';

import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

// utils
import chartsUtils, { DataCode, Dataset } from 'utils/charts.utils';

import { lineChartOptions } from './lineChartNewOptions';

// styles
import './lineChartNew.scss';

interface LineChartProps {
  title: string;
  options?: any;
  dataset: Dataset;
  dataCode: DataCode;
  selectedLocations: Array<string>;
  yearRange: Array<number>;
  onNoData: () => void;
  onCallback: (chart: React.MutableRefObject<null>) => void;
  onSelectedLocation: (name: string) => void;
  onUnSelectedLocation: (name: string) => void;
  roundValues?: boolean;
  pointFormat?: string;
}

const LineChartNew = (props: LineChartProps) => {
  const chartComponent = useRef(null);
  const [chartOptions, setChartOptions] = useState<any>(_.merge({}, lineChartOptions, props.options));
  //const [selectedSeries, setSelectedSeries] = useState<Array<string>>(['ch']);

  const getLineChartSeriesData = () => {
    const yearsData = chartsUtils.getYearRangeData(props.dataset, props.dataCode, props.yearRange[0], props.yearRange[1]);
    const localizations = chartsUtils.getLocalizations(props.dataset, props.dataCode);

    if (!localizations) {
      return null;
    }

    if (!yearsData) {
      return null;
    }

    let result: Array<any> = [];

    localizations.forEach((localization) => {
      let resultItem = {
        connectNulls: true,
        name: localization.code,
        data: [] as Array<any>, //Array<number>
        color: chartsUtils.getColor(localization.code),
        marker: {
          symbol: 'square',
        },

        events: {
          legendItemClick: function () {
            if (!this.visible) {
              props.onSelectedLocation(this.name.toLowerCase());
              //setSelectedSeries((selectedSeries) => [...selectedSeries, this.name.toLowerCase()]);
            } else {
              props.onUnSelectedLocation(this.name.toLowerCase());
              //setSelectedSeries((selectedSeries) => selectedSeries.filter((s) => s !== this.name.toLowerCase()));
            }
          },
        },

        //visible: selectedSeries.findIndex((s) => s === localization.code.toLowerCase()) >= 0,
        visible: props.selectedLocations.findIndex((s) => s === localization.code.toLowerCase()) >= 0,
      };

      yearsData.forEach((yearData) => {
        let localizationValue = yearData.data[localization.index];
        if (localizationValue && props.roundValues === true) {
          localizationValue = Math.round(localizationValue);
        }
        resultItem.data.push({ x: yearData.year, y: localizationValue });
      });

      result.push(resultItem);
    });

    return result;
  };

  const getYAxisSoftMaxValue = () => {
    if (!chartOptions.yAxis.maximums) {
      return null;
    }
    const maximum = chartOptions.yAxis.maximums.find((e) => e.dataset == props.dataset.code);
    if (!maximum) {
      return 80;
    }

    return maximum?.value;
  };

  const getYAxisMaxValue = (data) => {
    const softMaxValue = getYAxisSoftMaxValue();

    const dataValues = data.filter((d) => props.selectedLocations.indexOf(d.name.toLowerCase()) > -1);

    let maxValue = softMaxValue;

    dataValues.forEach((dv) => {
      dv.data.forEach((d) => {
        maxValue = d.y > maxValue ? d.y : maxValue;
      });
    });

    return maxValue;
  };

  const getTitle = () => {
    let result = props.title;

    result = result.replace('{DATASET}', props.dataset.name);
    result = result.replace('{FILTER}', chartsUtils.getDataCodeName(props.dataCode));
    result = result.replace('{YEAR_START}', props.yearRange[0].toString());
    result = result.replace('{YEAR_END}', props.yearRange[1].toString());

    return result;
  };

  const loadChart = () => {
    const data = getLineChartSeriesData();
    if (data == null) {
      props.onNoData();
      return;
    } else {
      //setNoData(false);
    }

    let pointFormat =
      '<table class="hicharttooltip-table"><tr><th>' +
      chartOptions.tooltip.headerLabel1 +
      '</th><th style="text-align:right;">' +
      chartOptions.tooltip.headerLabel2 +
      '</th></tr>' +
      `<tr class="hicharttooltip-table-values"><td>{point.x}</td><td style="text-align:right;">{point.y${
        props.pointFormat ? props.pointFormat : ''
      }}</td></tr></table>`;

    let newOptions = { ...chartOptions };

    newOptions = _.merge(newOptions, {
      exporting: {
        chartOptions: {
          title: {
            text: getTitle(),
          },
        },
      },
      tooltip: {
        pointFormat,
      },
      yAxis: [
        {
          title: {
            text: chartOptions.yAxis.title,
            style: {
              whiteSpace: 'nowrap',
            },
          },
          softMax: getYAxisSoftMaxValue(),
          max: getYAxisMaxValue(data),
        },
      ],
    });
    if (chartOptions.yAxis.labels) {
      newOptions.yAxis[0].labels = chartOptions.yAxis.labels;
    }
    if (chartOptions.xAxis && chartOptions.xAxis.plotLines) {
      newOptions.xAxis[0] = {
        ...newOptions.xAxis[0],
        plotLines: chartOptions.xAxis.plotLines,
      };
    }
    newOptions.series = data;

    setChartOptions(newOptions);
  };

  const handleCallback = (chart: any) => {
    props.onCallback(chartComponent);
  };

  useEffect(() => {
    loadChart();
  }, [props.dataCode, props.yearRange, props.dataset, props.selectedLocations]);

  return (
    <div className="lineChartNew">
      <div className="chart-title">{getTitle()}</div>
      <div className="lineChartNew-chart-wrapper">
        <div className="lineChartNew-chart">
          <HighchartsReact ref={chartComponent} highcharts={Highcharts} options={chartOptions} callback={handleCallback} />
        </div>
      </div>
    </div>
  );
};

export default LineChartNew;
